import { Component, Input, Output, EventEmitter } from "@angular/core";
import { getDisplayVolumeDiscountTiers } from "../utils/volume-discount.utils";
import { DateUtils } from "../utils/date.utils";
import {
    TableVariant,
    SUPPLY_TYPE_PRE_ORDER,
    DEFAULT_MOQ,
} from "../types/variant.types";
import { AVAILABILITY_TEXT } from "../constants/availability.constants";
import { NotificationService } from "../../core/providers/notification/notification.service";
import {
    getMinQ as getVariantMinQ,
    getMaxQ as getVariantMaxQ,
    getOrderStep,
    getBundleLabel as resolveBundleLabel,
    getInitialQuantity,
    normalizePackageQuantity,
    PackageProductContext,
} from "../utils/pricing.utils";

@Component({
    selector: "vsf-variant-table",
    templateUrl: "./variant-table.component.html",
    styleUrls: ["./variant-table.component.scss"],
})
export class VariantTableComponent {
    readonly TEXT = AVAILABILITY_TEXT;

    @Input() productPage = false;
    @Input() busy = false;
    @Input() maxMov: number | null = null;
    @Input() variants: TableVariant[] = [];
    @Input() quantityMap: { [key: string]: number } = {};
    @Input() title: string;
    @Input() quantityPerBox: number | null = null;
    @Output() quantityChange = new EventEmitter<{
        variantId: string;
        quantity: number;
    }>();
    @Output() addToCartEvent = new EventEmitter<{
        variant: TableVariant;
        quantity: number;
    }>();

    constructor(private notificationService: NotificationService) {}

    readonly expandedOffers = new Set<string>();
    toggleOffer(id: string): void { this.expandedOffers.has(id) ? this.expandedOffers.delete(id) : this.expandedOffers.add(id); }
    get totalAvailable(): number { return this.variants.reduce((sum, v) => sum + Math.max(0, this.getAvailableQuantity(v)), 0); }

    trackPriceTier(_index: number, tier: { price: number; mov: number }): string {
        return `${tier.mov}:${tier.price}`;
    }

    private readonly tierCache = new WeakMap<TableVariant, { key: string; tiers: { price: number; mov: number }[] }>();

    priceTiers(variant: TableVariant): { price: number; mov: number }[] {
        const baseMov = variant.pricing?.mov ?? 0;
        const key = JSON.stringify([variant.priceWithTax, baseMov, variant.pricing?.volumeDiscounts, this.maxMov]);
        const cached = this.tierCache.get(variant);
        if (cached?.key === key) return cached.tiers;
        const tiers = [
            ...getDisplayVolumeDiscountTiers(variant.pricing?.volumeDiscounts)
                .filter(t => t.discountPercent < 100 && (this.maxMov === null || t.orderValueMin * 100 <= this.maxMov))
                .map(t => ({ price: Math.round(variant.priceWithTax * (1 - t.discountPercent / 100)), mov: Math.max(baseMov, t.orderValueMin * 100) })),
            { price: variant.priceWithTax, mov: baseMov }
        ].sort((a, b) => a.price - b.price);
        this.tierCache.set(variant, { key, tiers });
        return tiers;
    }

    get sortedVariants(): TableVariant[] {
        return [...this.variants].sort(
            (a, b) => this.productPage ? this.priceTiers(a)[0].price - this.priceTiers(b)[0].price : a.priceWithTax - b.priceWithTax
        );
    }

    getAvailableQuantity = (variant: TableVariant): number => {
        const stockLevel = variant?.stockLevels?.[0];
        if (!stockLevel) {
            return 0;
        }
        return stockLevel.stockOnHand - stockLevel.stockAllocated;
    };

    isPreOrder = (variant: TableVariant): boolean =>
        variant?.pricing?.supplyType === SUPPLY_TYPE_PRE_ORDER;

    getAvailabilityDisplay = (variant: TableVariant): string => {
        if (this.isPreOrder(variant)) {
            return AVAILABILITY_TEXT.PRE_ORDER;
        }
        const qty = this.getAvailableQuantity(variant);
        if (qty <= 0) {
            return AVAILABILITY_TEXT.OUT_OF_STOCK;
        }
        return `${qty} ${AVAILABILITY_TEXT.UNITS_SUFFIX}`;
    };

    getMoq = (variant: TableVariant): number =>
        variant?.moq ?? variant?.pricing?.moq ?? DEFAULT_MOQ;

    getMinQ = (variant: TableVariant): number => getVariantMinQ(variant);

    getMaxQ = (variant: TableVariant): number | null => getVariantMaxQ(variant);

    getBundleLabel = (variant: TableVariant): string | null =>
        resolveBundleLabel(variant, this.getProductContext());

    private getProductContext(): PackageProductContext | null {
        if (this.quantityPerBox == null || this.quantityPerBox <= 0) {
            return null;
        }
        return { customFields: { quantityPerBox: this.quantityPerBox } };
    }

    getQuantityInfoText = (variant: TableVariant): string => {
        const minQ = this.getMinQ(variant);
        const maxQ = this.getMaxQ(variant);
        
        if (maxQ !== null && maxQ > 0) {
            return `${AVAILABILITY_TEXT.MIN_ORDER_PREFIX} ${minQ} ${AVAILABILITY_TEXT.QUANTITY_SEPARATOR} ${AVAILABILITY_TEXT.MAX_ORDER_PREFIX} ${maxQ}`;
        }
        return `${AVAILABILITY_TEXT.MIN_ORDER_PREFIX} ${minQ}`;
    };

    hasMinQ = (variant: TableVariant): boolean =>
        !!(variant?.pricingProduct?.minQ);

    private getVariantById = (variantId: string): TableVariant | undefined =>
        this.variants.find((v) => v.id === variantId);

    private getMoqByVariantId = (variantId: string): number => {
        const variant = this.getVariantById(variantId);
        return variant ? this.getMoq(variant) : DEFAULT_MOQ;
    };

    decreaseQuantity = (variantId: string): void => {
        const variant = this.getVariantById(variantId);
        if (!variant) return;
        const minQ = this.getMinQ(variant);
        const currentQty = this.quantityMap[variantId] ?? getInitialQuantity(variant, this.getProductContext());
        const decrementStep = getOrderStep(variant, this.getProductContext());
        const newQty = currentQty - decrementStep;
        
        if (newQty >= minQ) {
            this.quantityMap[variantId] = newQty;
            this.quantityChange.emit({
                variantId,
                quantity: this.quantityMap[variantId],
            });
        } else {
            this.showMoqAlert(minQ);
        }
    };

    private showMoqAlert = (moq: number): void => {
        this.notificationService
            .error(`${AVAILABILITY_TEXT.MOQ_ALERT} ${moq}`)
            .subscribe();
    };

    increaseQuantity = (variantId: string): void => {
        const variant = this.getVariantById(variantId);
        if (!variant) return;
        const minQ = this.getMinQ(variant);
        const maxQ = this.getMaxQ(variant);
        
        if (this.quantityMap[variantId] === undefined) {
            this.quantityMap[variantId] = getInitialQuantity(variant, this.getProductContext());
        }

        if (maxQ && this.quantityMap[variantId] >= maxQ) {
            return;
        }

        const incrementStep = getOrderStep(variant, this.getProductContext());

        this.quantityMap[variantId] += incrementStep;
        this.quantityChange.emit({
            variantId,
            quantity: this.quantityMap[variantId],
        });
    };

    onQuantityChange = (value: string, variantId: string): void => {
        const variant = this.getVariantById(variantId);
        if (!variant) return;
        const minQ = this.getMinQ(variant);
        const maxQ = this.getMaxQ(variant);
        const parsedValue = parseInt(value, 10);
        
        if (!isNaN(parsedValue) && parsedValue >= minQ) {
            const finalQuantity = normalizePackageQuantity(
                parsedValue,
                variant,
                this.getProductContext(),
            );
            this.quantityMap[variantId] = finalQuantity;
            this.quantityChange.emit({ variantId, quantity: finalQuantity });
        } else {
            const initialQuantity = getInitialQuantity(variant, this.getProductContext());
            this.showMoqAlert(minQ);
            this.quantityMap[variantId] = initialQuantity;
            this.quantityChange.emit({ variantId, quantity: initialQuantity });
        }
    };

    getQuantity = (variantId: string): number => {
        const variant = this.getVariantById(variantId);
        if (!variant) return DEFAULT_MOQ;
        if (this.quantityMap[variantId] === undefined) {
            this.quantityMap[variantId] = getInitialQuantity(variant, this.getProductContext());
        }
        return this.quantityMap[variantId];
    };

    addToCart = (variant: TableVariant, quantity: number): void => {
        this.addToCartEvent.emit({ variant, quantity });
    };

    getShippingDateDisplay = (variant: TableVariant): string =>
        DateUtils.getVariantShippingDateDisplay(variant);
}
